

import React, { useState } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import { 
  Button, 
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  CircularProgress,
  Avatar
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { useUser } from './UserContext';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Enhanced styled components
const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: 25,
  textTransform: 'none',
  fontWeight: 600,
  padding: theme.spacing(1.5, 3),
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  background: 'rgba(255, 255, 255, 0.2)',
  backdropFilter: 'blur(10px)',
  border: '2px solid rgba(255, 255, 255, 0.3)',
  color: 'blue',
  boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)',
  '&:hover': {
    background: 'rgba(255, 255, 255, 0.3)',
    border: '2px solid rgba(255, 255, 255, 0.5)',
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 25px rgba(0, 0, 0, 0.2)',
  },
  '&:active': {
    transform: 'translateY(0)',
  }
}));

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: 24,
    padding: theme.spacing(1),
    background: 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(20px)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
  },
}));

const GradientDialogTitle = styled(DialogTitle)(({ theme }) => ({
  background: 'linear-gradient(135deg, #f44336, #e53935)',
  color: 'white',
  textAlign: 'center',
  borderRadius: '20px 20px 0 0',
  margin: theme.spacing(-1, -1, 0, -1),
  padding: theme.spacing(3),
}));

const SupervisorLogOut = ({ username }) => {
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const navigate = useNavigate();
  const { setUsername: setGlobalUsername } = useUser();

  const handleLogoutClick = () => {
    setConfirmDialogOpen(true);
  };

  const handleConfirmLogout = async () => {
    setIsLoggingOut(true);
    
    try {
      const response = await axios.post(`${API_BASE_URL}/manhoursupervisorlogout`, { username });

      if (response.status === 200) {
        setGlobalUsername("");
        localStorage.removeItem('username');
        // Clear session storage as well
        sessionStorage.clear();
        navigate("/");
      } else {
        console.warn("Logout transaction did not complete as expected.");
        alert("Logout was not completed successfully. Please try again.");
      }
    } catch (error) {
      console.error("Error logging out:", error);
      alert("An error occurred during logout. Please try again.");
    } finally {
      setIsLoggingOut(false);
      setConfirmDialogOpen(false);
    }
  };

  const handleCancelLogout = () => {
    setConfirmDialogOpen(false);
  };

  return (
    <>
      <StyledButton
        onClick={handleLogoutClick}
        startIcon={<LogoutIcon />}
        disabled={isLoggingOut}
        size="large"
      >
        {isLoggingOut ? 'Logging out...' : 'Logout'}
      </StyledButton>

      <StyledDialog 
        open={confirmDialogOpen} 
        onClose={handleCancelLogout}
        maxWidth="sm"
        fullWidth
      >
        <GradientDialogTitle>
          <Avatar 
            sx={{ 
              bgcolor: 'rgba(255, 255, 255, 0.2)', 
              mx: 'auto', 
              mb: 2, 
              width: 64, 
              height: 64 
            }}
          >
            <LogoutIcon fontSize="large" />
          </Avatar>
          <Typography variant="h5" fontWeight="bold">
            Confirm Logout
          </Typography>
        </GradientDialogTitle>
        
        <DialogContent sx={{ textAlign: 'center', py: 4 }}>
          <Box display="flex" alignItems="center" justifyContent="center" gap={2} mb={2}>
            <Avatar sx={{ bgcolor: '#1976d2' }}>
              <PersonIcon />
            </Avatar>
            <Box textAlign="left">
              <Typography variant="h6" fontWeight="bold">
                {username}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Supervisor
              </Typography>
            </Box>
          </Box>
          
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            Are you sure you want to logout from the supervisor dashboard?
          </Typography>
          

        </DialogContent>
        
        <DialogActions sx={{ justifyContent: 'center', pb: 3, gap: 2 }}>
          <Button
            onClick={handleCancelLogout}
            variant="outlined"
            size="large"
            disabled={isLoggingOut}
            sx={{
              borderRadius: 3,
              textTransform: 'none',
              fontWeight: 600,
              minWidth: 120,
              borderWidth: 2,
              '&:hover': {
                borderWidth: 2,
              }
            }}
          >
            Cancel
          </Button>
          
          <Button
            onClick={handleConfirmLogout}
            variant="contained"
            color="error"
            size="large"
            disabled={isLoggingOut}
            startIcon={isLoggingOut ? <CircularProgress size={20} color="inherit" /> : <LogoutIcon />}
            sx={{
              borderRadius: 3,
              textTransform: 'none',
              fontWeight: 600,
              minWidth: 120,
              background: 'linear-gradient(45deg, #f44336, #e53935)',
              boxShadow: '0 4px 15px rgba(244, 67, 54, 0.4)',
              '&:hover': {
                background: 'linear-gradient(45deg, #e53935, #d32f2f)',
                boxShadow: '0 6px 20px rgba(244, 67, 54, 0.6)',
              }
            }}
          >
            {isLoggingOut ? 'Logging Out...' : 'Logout'}
          </Button>
        </DialogActions>
      </StyledDialog>
    </>
  );
};

SupervisorLogOut.propTypes = {
  username: PropTypes.string.isRequired,
};

export default SupervisorLogOut;